import cn from '@sindresorhus/class-names';
import Link from 'next/link';
import Pagination from 'rc-pagination';
import React, {memo} from 'react';
import {UrlObject} from 'url';

import {Button} from '../Button';
import {Icon} from '../Icon';
import {ChevronLeftSmallSvg, ChevronRightSmallSvg} from '../SvgIcons';
import styles from './Pagination.module.scss';

type Props = {
  current: number;
  pageSize: number;
  total: number;
  prefetch?: boolean;
  onChange: ((page: number, pageSize: number) => void) | undefined;
  customHref?: (page: number) => string | UrlObject;
};

export const PaginationComponent = memo<Props>(
  ({current, pageSize, total, prefetch, onChange, customHref}) => {
    const totalPages = Math.ceil(total / pageSize);

    const paginationItemRender = (
      page: number,
      type: string,
      element: React.ReactNode,
    ) => {
      const isFirstOrLastPage = current === 1 || current === totalPages;
      const isActive = page === current;

      switch (type) {
        case 'page':
          return customHref ? (
            <Link
              data-testid="paginationComponentPageLink"
              className={cn(styles.page, {
                [styles.activePage]: isActive,
              })}
              href={customHref(page)}
              onClick={(event) => event.preventDefault()}
              aria-label={isActive ? undefined : `Перейти на страницу ${page}`}
              aria-current={isActive && 'page'}
              tabIndex={-1}
              prefetch={prefetch}
            >
              {page}
            </Link>
          ) : (
            <a
              className={cn(styles.page, {
                [styles.activePage]: isActive,
              })}
              aria-label={isActive ? undefined : `Перейти на страницу ${page}`}
              aria-current={isActive && 'page'}
              tabIndex={-1}
            >
              {page}
            </a>
          );

        case 'prev':
          return (
            <Button
              className={cn(styles.backButton, {
                [styles.fullFlexBasis]: isFirstOrLastPage,
              })}
              kind={'outline'}
              label={'Назад'}
              aria-label="Перейти на предыдущую страницу"
              tabIndex={-1}
              beforeIcon={
                <Icon svg={ChevronLeftSmallSvg} className={styles.chevron} />
              }
            />
          );

        case 'next':
          return (
            <Button
              className={cn(styles.forwardButton, {
                [styles.fullFlexBasis]: isFirstOrLastPage,
              })}
              kind={'outline'}
              label={'Вперёд'}
              aria-label="Перейти на следующую страницу"
              tabIndex={-1}
              afterIcon={
                <Icon svg={ChevronRightSmallSvg} className={styles.chevron} />
              }
            />
          );

        case 'jump-prev':
          return customHref ? (
            <Link
              className={cn(styles.page, styles.dots)}
              href={customHref(current - 3)}
              aria-label="Перейти на 3 страницы назад"
              prefetch={prefetch}
            />
          ) : (
            <a
              className={cn(styles.page, styles.dots)}
              aria-label="Перейти на 3 страницы назад"
            ></a>
          );

        case 'jump-next':
          return customHref ? (
            <Link
              className={cn(styles.page, styles.dots)}
              href={customHref(current + 3)}
              aria-label="Перейти на 3 страницы вперед"
              prefetch={prefetch}
            />
          ) : (
            <a
              className={cn(styles.page, styles.dots)}
              aria-label="Перейти на 3 страницы вперед"
            ></a>
          );

        default:
          return element;
      }
    };

    return (
      <Pagination
        className={styles.wrapper}
        itemRender={paginationItemRender}
        hideOnSinglePage
        current={current}
        showLessItems
        pageSize={pageSize}
        total={total}
        onChange={onChange}
        showTitle={false}
      />
    );
  },
);

PaginationComponent.displayName = 'PaginationComponent';
